import { useState, useMemo, useCallback, useEffect } from "react";
import { doc, setDoc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useI18n } from "@/contexts/I18nContext";
import { useFirestoreCollection } from "@/hooks/useFirestore";
import { Card } from "@/components/ui/Card";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { Avatar } from "@/components/ui/Avatar";
import { EmptyState } from "@/components/ui/EmptyState";
import { CheckListItem } from "@/components/ui/CheckListItem";
import { MealCard } from "@/components/cards/MealCard";
import { WeekendCard } from "@/components/cards/WeekendCard";
import { PostCardCompact } from "@/components/ui/PostCardCompact";
import { AddMemberModal } from "@/components/features/AddMemberModal";
import { skipMealType, unskipMealType, sendMealVoteReminder } from "@/lib/mealActions";
import { geocodeArea } from "@/lib/geocodeArea";
import { relativeDate } from "@/lib/humanDate";
import { fromTimestamp } from "@/lib/firestore";
import { updateDocument } from "@/lib/firestore";
import { eventOverlapsDay, isAllDayEvent } from "@/lib/calendar";
import {
  filterAssignedChoresByHousehold,
  isDoneNow,
  toggleChoreCompletion,
} from "@/lib/chores";
import { DASHBOARD_SECTIONS } from "@/lib/constants";
import type { DashboardSectionId } from "@/lib/constants";
import type {
  CalendarEvents,
  Responsibility,
  ResponsibilityClaim,
  AssignedChore,
  ShoppingListItem,
  GroceryListItem,
  HouseholdPost,
  HouseholdChat,
  MealSuggestion,
  GroceryPrice,
  WeekendSuggestion,
  WeekendMoodVote,
} from "@/lib/models";
import { Link, useNavigate } from "react-router-dom";

export function DashboardPage() {
  const { profile, household, householdId, allHouseholds, switchHousehold, members, loading } =
    useHouseholdContext();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [todayDayOffset, setTodayDayOffset] = useState(0);
  const [showHouseholdMenu, setShowHouseholdMenu] = useState(false);

  // Today's events
  const { data: allEvents } =
    useFirestoreCollection<CalendarEvents>(
      householdId ? `households/${householdId}/events` : null
    );

  // Responsibilities
  const { data: responsibilities } =
    useFirestoreCollection<Responsibility>(
      householdId ? `households/${householdId}/responsibilities` : null
    );

  const { data: claims } =
    useFirestoreCollection<ResponsibilityClaim>(
      householdId ? `households/${householdId}/responsibilityClaims` : null
    );

  // Assigned chores for current user. Subcollection is per-member
  // cross-household; filter to the active household so the dashboard
  // chore list matches the household context. Mirrors iOS PR #111 +
  // Android `feat/android-rewards-firestore` commit 68292ee. See
  // [[tasks/daily-chore-reset]].
  const { data: myChoresRaw } =
    useFirestoreCollection<AssignedChore>(
      profile?.uid ? `member_profiles/${profile.uid}/assigned_chores` : null
    );
  const myChores = useMemo(
    () => filterAssignedChoresByHousehold(myChoresRaw, householdId),
    [myChoresRaw, householdId],
  );

  // Shopping list
  const { data: shoppingItems } =
    useFirestoreCollection<ShoppingListItem>(
      householdId ? `households/${householdId}/shoppingListItems` : null
    );

  // Grocery list
  const { data: groceryItems } =
    useFirestoreCollection<GroceryListItem>(
      householdId ? `households/${householdId}/groceryListItems` : null
    );

  // Grocery prices
  const { data: groceryPricesDocs } = useFirestoreCollection<GroceryPrice>(
    householdId ? `households/${householdId}/grocery_prices` : null
  );

  // Meal suggestions
  const { data: allMeals } = useFirestoreCollection<MealSuggestion>(
    householdId ? `households/${householdId}/mealSuggestions` : null
  );

  // Weekend suggestions
  const { data: allWeekendSuggestions } =
    useFirestoreCollection<WeekendSuggestion>(
      householdId ? `households/${householdId}/weekendSuggestions` : null
    );

  // Weekend mood votes — drives the inline mood-picker affordance.
  const { data: weekendMoodVotes } =
    useFirestoreCollection<WeekendMoodVote>(
      householdId ? `households/${householdId}/weekendMoodVotes` : null
    );

  const myMoodVote = useMemo(
    () => weekendMoodVotes.find((v) => v.uid === profile?.uid),
    [weekendMoodVotes, profile?.uid]
  );
  const hasVotedMoodsThisWeek = !!myMoodVote;
  const [selectedDashMoods, setSelectedDashMoods] = useState<Set<string>>(
    new Set()
  );
  const [savingDashMood, setSavingDashMood] = useState(false);

  const DASH_MOOD_OPTIONS = useMemo(
    () => [
      { label: "Outdoors", emoji: "\u{1F332}" },
      { label: "Chill at home", emoji: "\u{1F6CB}️" },
      { label: "Food & drinks", emoji: "\u{1F37D}️" },
      { label: "Adventure", emoji: "\u{1F3D4}️" },
      { label: "Arts & culture", emoji: "\u{1F3A8}" },
      { label: "Sports & active", emoji: "⚽" },
      { label: "Shopping", emoji: "\u{1F6CD}️" },
      { label: "Surprise me", emoji: "\u{1F3B2}" },
    ],
    []
  );

  const toggleDashMood = useCallback((label: string) => {
    setSelectedDashMoods((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const saveDashMood = useCallback(async () => {
    if (!householdId || !profile?.uid || selectedDashMoods.size === 0) return;
    setSavingDashMood(true);
    try {
      await setDoc(
        doc(db, `households/${householdId}/weekendMoodVotes`, profile.uid),
        {
          moods: Array.from(selectedDashMoods),
          voted_at: Timestamp.now(),
        }
      );
    } catch (err) {
      console.error("Failed to save mood:", err);
    } finally {
      setSavingDashMood(false);
    }
  }, [householdId, profile?.uid, selectedDashMoods]);

  // Recent posts
  const { data: allPosts } = useFirestoreCollection<HouseholdPost>(
    householdId ? `households/${householdId}/posts` : null
  );

  // Recent chats
  const { data: allChats } = useFirestoreCollection<HouseholdChat>(
    householdId ? `households/${householdId}/chats` : null
  );

  // Section order from localStorage
  const [sectionOrder, setSectionOrder] = useState<DashboardSectionId[]>(
    DASHBOARD_SECTIONS.map((s) => s.id)
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("dashboardSectionOrder");
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as DashboardSectionId[];
          // Validate all IDs are valid
          const validIds = new Set(DASHBOARD_SECTIONS.map((s) => s.id));
          if (parsed.every((id) => validIds.has(id)) && parsed.length === validIds.size) {
            setSectionOrder(parsed);
          }
        } catch {
          // ignore invalid JSON
        }
      }
    }
  }, []);

  // Filter events for the day at todayDayOffset (0 = today, 1 = tomorrow, ...)
  // Uses the shared range-overlap helper so the dashboard agrees with the
  // calendar view: this both fixes the zero-duration midnight all-day case and
  // makes multi-day events show on every day they span (the start-only check
  // this replaced only matched day 1).
  const todayEvents = useMemo(() => {
    const now = new Date();
    const day = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + todayDayOffset
    );

    return allEvents.filter((event) =>
      eventOverlapsDay(
        fromTimestamp(event.start),
        fromTimestamp(event.end),
        day
      )
    );
  }, [allEvents, todayDayOffset]);

  const dayOffsetLabel = useMemo(() => {
    if (todayDayOffset === 0) return t("dashboard.events.title");
    if (todayDayOffset === 1) return t("dashboard.events.titleTomorrow");
    const target = new Date();
    target.setDate(target.getDate() + todayDayOffset);
    return target.toLocaleDateString(locale, { weekday: "long" });
  }, [todayDayOffset, t, locale]);

  // Hour-based meal filter — scope to the *next* meal of the day so
  // the dashboard rail is purposeful (breakfast in the morning, lunch
  // mid-day, dinner in the evening) rather than a six-card dump.
  // Mirrors iOS `Dashboard.swift:918-920` `nextMealType` calc.
  const nextMealType = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 10) return "breakfast";
    if (hour < 14) return "lunch";
    return "dinner";
  }, []);

  const todayMeals = useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

    // Strict filter — only meals of `nextMealType` for today, not
    // skipped. Mirrors iOS `nextMealSuggestions` at
    // `Dashboard.swift:931-940`. An earlier version had a fallback
    // that surfaced other meal types when this one was empty; that
    // masked the post-skip blank-out state and let unskipped meals
    // of other types reappear in the rail right after the user
    // skipped the current one.
    return allMeals
      .filter(
        (meal) =>
          meal.suggested_date_str === todayStr &&
          meal.meal_type?.toLowerCase() === nextMealType &&
          !meal.is_skipped,
      )
      .sort((a, b) => {
        if (a.is_chosen !== b.is_chosen) return a.is_chosen ? -1 : 1;
        return a.meal_type.localeCompare(b.meal_type);
      })
      .slice(0, 4);
  }, [allMeals, nextMealType]);

  // Sort posts by created_at descending, take 3
  const recentPosts = useMemo(() => {
    return [...allPosts]
      .sort((a, b) => {
        const aTime = fromTimestamp(a.created_at)?.getTime() ?? 0;
        const bTime = fromTimestamp(b.created_at)?.getTime() ?? 0;
        return bTime - aTime;
      })
      .slice(0, 3);
  }, [allPosts]);

  // Sort chats by last_reply_at or created_at descending, take 3
  const recentChats = useMemo(() => {
    return [...allChats]
      .sort((a, b) => {
        const aTime =
          fromTimestamp(a.last_reply_at)?.getTime() ??
          fromTimestamp(a.created_at)?.getTime() ??
          0;
        const bTime =
          fromTimestamp(b.last_reply_at)?.getTime() ??
          fromTimestamp(b.created_at)?.getTime() ??
          0;
        return bTime - aTime;
      })
      .slice(0, 3);
  }, [allChats]);

  // Growth card dismiss state
  const [growthDismissed, setGrowthDismissed] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [inlineAreaCity, setInlineAreaCity] = useState("");
  const [inlineAreaZip, setInlineAreaZip] = useState("");
  const [savingInlineArea, setSavingInlineArea] = useState(false);
  const [mealActionPending, setMealActionPending] = useState(false);
  const [mealReminderSent, setMealReminderSent] = useState(false);
  const [showSkipConfirm, setShowSkipConfirm] = useState(false);
  const [showRemindConfirm, setShowRemindConfirm] = useState(false);

  // iOS gates the meal action buttons on adult member status; web
  // mirrors the same rule. `elderly_parent` counts as adult for
  // these gates (PR #1 alignment).
  const isCurrentUserAdult =
    profile?.memberType === "adult" || profile?.memberType === "elderly_parent";

  const handleSkipNextMeal = useCallback(async () => {
    if (!householdId) return;
    setMealActionPending(true);
    try {
      await skipMealType(householdId, nextMealType);
    } catch (err) {
      console.error("Failed to skip meal:", err);
    } finally {
      setMealActionPending(false);
    }
  }, [householdId, nextMealType]);

  const handleUnskipNextMeal = useCallback(async () => {
    if (!householdId) return;
    setMealActionPending(true);
    try {
      await unskipMealType(householdId, nextMealType);
    } catch (err) {
      console.error("Failed to unskip meal:", err);
    } finally {
      setMealActionPending(false);
    }
  }, [householdId, nextMealType]);

  const handleRemindMealVote = useCallback(async () => {
    if (!householdId) return;
    setMealActionPending(true);
    try {
      await sendMealVoteReminder(householdId, nextMealType);
      setMealReminderSent(true);
      setTimeout(() => setMealReminderSent(false), 4000);
    } catch (err) {
      console.error("Failed to send meal reminder:", err);
    } finally {
      setMealActionPending(false);
    }
  }, [householdId, nextMealType]);

  const handleSaveInlineArea = useCallback(async () => {
    if (!householdId) return;
    const city = inlineAreaCity.trim();
    const zip = inlineAreaZip.trim();
    if (!city && !zip) return;
    setSavingInlineArea(true);
    try {
      // Geocode in parallel with the city/zip write so the user
      // doesn't wait on a flaky third-party for the primary save.
      // Lat/lng update follows when the geocode resolves.
      const geocodePromise = geocodeArea({ city, zip });
      await updateDoc(doc(db, "households", householdId), {
        location_city: city || null,
        location_zip: zip || null,
      });
      const coords = await geocodePromise;
      if (coords) {
        await updateDoc(doc(db, "households", householdId), {
          location_lat: coords.lat,
          location_lng: coords.lng,
        });
      }
    } catch (err) {
      console.error("Failed to save area:", err);
    } finally {
      setSavingInlineArea(false);
    }
  }, [householdId, inlineAreaCity, inlineAreaZip]);

  // Filter weekend suggestions to current weekend
  const weekendSuggestions = useMemo(() => {
    const now = new Date();
    const day = now.getDay();
    const offset = day === 0 ? -1 : 6 - day;
    const sat = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    sat.setHours(0, 0, 0, 0);
    const sunEnd = new Date(sat.getTime() + 2 * 24 * 60 * 60 * 1000);

    return allWeekendSuggestions
      .filter((s) => {
        const ts = s.weekend_of;
        if (!ts) return false;
        const date =
          typeof (ts as { toDate?: () => Date }).toDate === "function"
            ? (ts as { toDate: () => Date }).toDate()
            : (ts as { seconds?: number }).seconds != null
              ? new Date((ts as { seconds: number }).seconds * 1000)
              : null;
        if (!date) return false;
        return date >= sat && date < sunEnd;
      })
      .sort((a, b) => {
        if (a.is_chosen !== b.is_chosen) return a.is_chosen ? -1 : 1;
        const aUp = Object.values(a.votes ?? {}).filter((v) => v === true).length;
        const bUp = Object.values(b.votes ?? {}).filter((v) => v === true).length;
        return bUp - aUp;
      })
      .slice(0, 3);
  }, [allWeekendSuggestions]);

  // Incomplete shopping / grocery counts
  const shoppingCount = shoppingItems.filter((i) => !i.isCompleted).length;
  const activeGroceryItems = groceryItems.filter((i) => !i.isCompleted);
  const groceryCount = activeGroceryItems.length;

  // Grocery price map + estimated total
  const groceryPriceMap = useMemo(() => {
    const map: Record<string, GroceryPrice> = {};
    for (const price of groceryPricesDocs) {
      if (price.uid) {
        map[price.uid.toLowerCase().trim()] = price;
      }
    }
    return map;
  }, [groceryPricesDocs]);

  function normalizeItemName(title: string): string {
    let result = title.toLowerCase().trim();
    result = result.replace(/^\d+(\.\d+)?\s*/, "");
    const units = ["lb", "lbs", "oz", "gal", "gallon", "dozen", "bunch", "bag", "box", "can", "jar", "pack", "pkg"];
    const unitPattern = new RegExp(`^(${units.join("|")})(s?)\\s+`);
    result = result.replace(unitPattern, "");
    return result.trim();
  }

  const groceryWithPrices = useMemo(() => {
    return activeGroceryItems.map((item) => {
      const normalized = normalizeItemName(item.title);
      const price = groceryPriceMap[normalized] ?? groceryPriceMap[item.title.toLowerCase().trim()];
      return { item, price: price?.cheapest_price ?? null, store: price?.store ?? price?.cheapest_store ?? price?.source ?? null };
    });
  }, [activeGroceryItems, groceryPriceMap]);

  const groceryEstimatedTotal = useMemo(() => {
    return groceryWithPrices.reduce((sum, g) => sum + (g.price ?? 0), 0);
  }, [groceryWithPrices]);

  // Incomplete chores — date-aware predicate so daily chores reset at UTC midnight
  // without any server-side job. See [[tasks/daily-chore-reset]].
  const incompleteChores = myChores.filter((c) => !isDoneNow(c));

  // Helper: find member by uid
  const findMember = (uid: string) =>
    members.find((m) => m.uid === uid);

  const handleToggleChore = async (chore: AssignedChore) => {
    if (!profile?.uid || !chore.uid) return;
    await toggleChoreCompletion({
      chore,
      assigneeUid: profile.uid,
      completerUid: profile.uid,
    });
  };

  const handleMealVote = useCallback(
    async (meal: MealSuggestion, isUpvote: boolean) => {
      if (!meal.uid || !householdId || !profile?.uid) return;
      const ref = doc(
        db,
        `households/${householdId}/mealSuggestions`,
        meal.uid
      );
      const currentVote = meal.votes?.[profile.uid];
      if (currentVote === isUpvote) {
        const { [profile.uid]: _, ...rest } = meal.votes ?? {};
        await updateDoc(ref, { votes: rest });
      } else {
        await updateDoc(ref, { [`votes.${profile.uid}`]: isUpvote });
      }
    },
    [householdId, profile?.uid]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  const greeting = getGreeting(t);
  const name =
    profile?.nickname ?? profile?.name ?? t("dashboard.greeting.defaultName");

  // ---- Section render functions ----

  function renderTodaySection() {
    return (
      <section key="today">
        <div className="flex items-center justify-between px-1 mb-2">
          <div className="flex items-center gap-2">
            <span aria-hidden="true">📅</span>
            <h2 className="font-bold text-[length:var(--font-section-title)]">
              {dayOffsetLabel}
            </h2>
            <span className="inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-gray-200 dark:bg-white/10 px-1.5 text-xs text-gray-600 dark:text-gray-400">
              {todayEvents.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setTodayDayOffset((v) => Math.max(0, v - 1))}
              disabled={todayDayOffset === 0}
              aria-label={t("dashboard.events.previousDay")}
              className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setTodayDayOffset((v) => Math.min(6, v + 1))}
              disabled={todayDayOffset === 6}
              aria-label={t("dashboard.events.nextDay")}
              className="p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>
        {todayEvents.length === 0 ? (
          <Card className="mt-2">
            <EmptyState icon={"☀️"} title={t("dashboard.events.empty.title")} subtitle={t("dashboard.events.empty.subtitle")} />
          </Card>
        ) : (
          <div className="mt-2 space-y-2">
            {todayEvents.map((event) => {
              const start = fromTimestamp(event.start);
              const end = fromTimestamp(event.end);
              const allDay = isAllDayEvent(event.all_day, start, end);
              return (
                <Card key={event.uid}>
                  <div className="flex items-start gap-3">
                    <div className="w-1 self-stretch rounded-full bg-[color:var(--accent-color)]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {event.summary ?? event.text ?? t("dashboard.event")}
                      </p>
                      {allDay ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {t("events.allDayBadge")}
                        </p>
                      ) : start && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {start.toLocaleTimeString(locale, {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </p>
                      )}
                      {event.location && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">
                          {event.location}
                        </p>
                      )}
                      {event.sync_source && event.sync_source !== "zoey" && (
                        <span className="inline-block mt-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400">
                          {event.sync_source}
                        </span>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  function renderResponsibilitiesSection() {
    return (
      <section key="responsibilities">
        <SectionHeader
          title={t("dashboard.responsibilities.title")}
          count={responsibilities.length}
          icon="✅"
        />
        {responsibilities.length === 0 ? (
          <Card className="mt-2">
            <EmptyState
              icon={"✅"}
              title={t("dashboard.responsibilities.empty.title")}
            />
          </Card>
        ) : (
          <div className="mt-2 space-y-2">
            {responsibilities.slice(0, 5).map((resp) => {
              const matchingClaim = claims.find(
                (c) => c.responsibilityId === resp.uid
              );
              const claimer = matchingClaim?.claimedBy
                ? findMember(matchingClaim.claimedBy)
                : null;

              return (
                <Card key={resp.uid}>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">
                        {resp.title}
                      </p>
                      {resp.scheduledTime && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {resp.scheduledTime}
                        </p>
                      )}
                      {matchingClaim && (
                        <p className="text-xs text-gray-400 mt-0.5 capitalize">
                          {matchingClaim.status}
                        </p>
                      )}
                    </div>
                    {claimer && (
                      <Avatar
                        src={claimer.profile_url}
                        alt={claimer.nickname ?? claimer.name ?? ""}
                        size={28}
                      />
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    );
  }

  function renderChoresSection() {
    return (
      <section key="chores">
        <SectionHeader title={t("dashboard.chores.title")} count={incompleteChores.length} icon="🧹" />
        {incompleteChores.length === 0 ? (
          <Card className="mt-2">
            <EmptyState
              icon={"\u{1F389}"}
              title={t("dashboard.chores.empty")}
            />
          </Card>
        ) : (
          <Card className="mt-2">
            <div className="divide-y divide-gray-100 dark:divide-white/10">
              {incompleteChores.slice(0, 5).map((chore) => (
                <CheckListItem
                  key={chore.uid}
                  text={chore.title}
                  checked={isDoneNow(chore)}
                  onChange={() => handleToggleChore(chore)}
                  trailing={
                    chore.points != null && chore.points > 0 ? (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        {chore.points} pt{chore.points === 1 ? "" : "s"}
                      </span>
                    ) : undefined
                  }
                />
              ))}
            </div>
          </Card>
        )}
      </section>
    );
  }

  function renderListsSection() {
    return (
      <section key="lists">
        <SectionHeader title={t("dashboard.lists.title")} icon="📝" />
        <div className="mt-2 grid grid-cols-2 gap-3">
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-[color:var(--accent-color)]">
                {shoppingCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t("dashboard.lists.shoppingItems")}</p>
            </div>
          </Card>
          <Card>
            <div className="text-center">
              <p className="text-2xl font-bold text-[color:var(--accent-color)]">
                {groceryCount}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t("dashboard.lists.groceryItems")}</p>
            </div>
            {groceryWithPrices.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-100 dark:border-white/10 space-y-1">
                {groceryWithPrices.slice(0, 3).map((g) => (
                  <div key={g.item.uid} className="flex items-center justify-between text-xs">
                    <span className="truncate flex-1 min-w-0 text-gray-600 dark:text-gray-400">{g.item.title}</span>
                    {g.price != null && g.price > 0 && (
                      <span className="text-green-600 dark:text-green-400 ml-1 shrink-0">${g.price.toFixed(2)}</span>
                    )}
                  </div>
                ))}
                {groceryEstimatedTotal > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400 pt-1">
                    {t("dashboard.lists.estimatedTotal", { total: groceryEstimatedTotal.toFixed(2) })}
                  </p>
                )}
              </div>
            )}
          </Card>
        </div>
      </section>
    );
  }

  function renderMealsSection() {
    // iOS rules (`Dashboard.swift:949-1063`) — three distinct states
    // for the meals section, all keyed off `nextMealType`:
    //   A. has unskipped meals → carousel + Skip + Remind (adults only)
    //   B. empty AND meal type is skipped → empty card + Undo (adults only)
    //   C. empty AND not skipped → "no ideas yet" empty card, no buttons
    // The "skipped" signal is "any doc of this meal_type today has
    // is_skipped=true" — equivalent to iOS's
    // `skippedMealTypes.contains(nextMealType)` but reconstructed
    // from the same Firestore reads web already has.
    const todayStr = (() => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    })();
    const nextTypeIsSkipped = allMeals.some(
      (m) =>
        m.meal_type?.toLowerCase() === nextMealType &&
        m.is_skipped === true &&
        m.suggested_date_str === todayStr,
    );
    const nextLabel = t(`dashboard.meals.title.${nextMealType}`);

    return (
      <section key="meals">
        <SectionHeader
          title={nextLabel}
          count={todayMeals.length}
          href="/meals"
          linkLabel={t("dashboard.meals.seeAll")}
          icon="🍽️"
        />
        {todayMeals.length === 0 && nextTypeIsSkipped ? (
          // State B — skipped, empty. Match iOS `DashEmptyCard`
          // density (`Dashboard.swift:1442-1458`): a single-line
          // muted card with small icon + caption text. Skipping
          // should "blank out" the section, not present a big
          // empty-state hero.
          <>
            <Card className="mt-2">
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <span aria-hidden="true">🍽</span>
                <span>{t("dashboard.meals.skippedEmpty.title")}</span>
              </div>
            </Card>
            {isCurrentUserAdult && (
              <button
                type="button"
                onClick={() => void handleUnskipNextMeal()}
                disabled={mealActionPending}
                className="mt-2 w-full py-2 rounded-lg text-sm font-medium bg-[color:var(--accent-color)]/10 text-[color:var(--accent-color)] hover:bg-[color:var(--accent-color)]/15 disabled:opacity-50 transition-colors"
              >
                ↶ {t("dashboard.meals.actions.undo")}
              </button>
            )}
          </>
        ) : todayMeals.length === 0 ? (
          // State C — no meals generated yet. Same compact
          // DashEmptyCard density as State B.
          <Card className="mt-2">
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span aria-hidden="true">🍽</span>
              <span>{t("dashboard.meals.empty.title")}</span>
            </div>
          </Card>
        ) : (
          // State A — has meals
          <>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {todayMeals.map((meal) => (
                <MealCard
                  key={meal.uid}
                  meal={meal}
                  userId={profile?.uid}
                  onVote={handleMealVote}
                  variant="compact"
                />
              ))}
            </div>

            {/* Skip + Remind — adults only. Mirrors iOS
                Dashboard.swift:1001-1061. Each button confirms
                before firing. */}
            {isCurrentUserAdult && (
              <div className="mt-2 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => setShowSkipConfirm(true)}
                  disabled={mealActionPending}
                  className="w-full py-2 rounded-lg text-sm font-medium border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 transition-colors"
                >
                  ⏭ {t("dashboard.meals.actions.skip")}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRemindConfirm(true)}
                  disabled={mealActionPending || mealReminderSent}
                  className={`w-full py-2 rounded-lg text-sm font-semibold text-white transition-opacity ${
                    mealReminderSent
                      ? "bg-emerald-500"
                      : "bg-[color:var(--accent-color)]"
                  } disabled:opacity-60`}
                >
                  {mealReminderSent
                    ? `✓ ${t("dashboard.meals.actions.reminded")}`
                    : `🔔 ${t("dashboard.meals.actions.remind")}`}
                </button>
              </div>
            )}
          </>
        )}

        {/* Confirmation dialogs — match iOS alerts. */}
        {showSkipConfirm && (
          <ConfirmDialog
            title={t("dashboard.meals.skipConfirm.title", {
              label: nextLabel.toLowerCase(),
            })}
            body={t("dashboard.meals.skipConfirm.body", {
              label: nextLabel.toLowerCase(),
            })}
            confirmLabel={t("dashboard.meals.actions.skip")}
            destructive
            onCancel={() => setShowSkipConfirm(false)}
            onConfirm={async () => {
              setShowSkipConfirm(false);
              await handleSkipNextMeal();
            }}
          />
        )}
        {showRemindConfirm && (
          <ConfirmDialog
            title={t("dashboard.meals.remindConfirm.title")}
            body={t("dashboard.meals.remindConfirm.body", {
              label: nextLabel.toLowerCase(),
            })}
            confirmLabel={t("dashboard.meals.actions.remind")}
            onCancel={() => setShowRemindConfirm(false)}
            onConfirm={async () => {
              setShowRemindConfirm(false);
              await handleRemindMealVote();
            }}
          />
        )}
      </section>
    );
  }

  function renderWeekendSection() {
    return (
      <section key="weekend">
        <SectionHeader
          title={t("dashboard.weekend.title")}
          count={weekendSuggestions.length}
          href="/weekend"
          linkLabel={t("dashboard.weekend.seeAll")}
          icon="🌤️"
        />

        {/* Inline mood picker — first vote of the week */}
        {!hasVotedMoodsThisWeek && (
          <Card className="mt-2">
            <p className="text-sm font-semibold mb-2">What are you in the mood for?</p>
            <div className="flex flex-wrap gap-1.5">
              {DASH_MOOD_OPTIONS.map((mood) => {
                const active = selectedDashMoods.has(mood.label);
                return (
                  <button
                    key={mood.label}
                    type="button"
                    onClick={() => toggleDashMood(mood.label)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color)] text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15"
                    }`}
                  >
                    {mood.emoji} {mood.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={saveDashMood}
              disabled={savingDashMood || selectedDashMoods.size === 0}
              className="mt-3 px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
            >
              {savingDashMood ? "Saving…" : "Save mood"}
            </button>
          </Card>
        )}

        {weekendSuggestions.length === 0 ? (
          <Card className="mt-2">
            {!household?.location_city && !household?.location_zip ? (
              // Inline area picker — set the household's neighborhood
              // directly from the dashboard rather than bouncing to
              // /weekend. Mirrors iOS `Dashboard.swift:1098-1119`.
              <div>
                <p className="font-semibold text-sm">
                  {t("dashboard.weekend.areaPicker.title")}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {t("dashboard.weekend.areaPicker.subtitle")}
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={inlineAreaCity}
                    onChange={(e) => setInlineAreaCity(e.target.value)}
                    placeholder={t("dashboard.weekend.areaPicker.cityPlaceholder")}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                  />
                  <input
                    type="text"
                    value={inlineAreaZip}
                    onChange={(e) => setInlineAreaZip(e.target.value)}
                    placeholder={t("dashboard.weekend.areaPicker.zipPlaceholder")}
                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSaveInlineArea}
                  disabled={
                    savingInlineArea ||
                    (!inlineAreaCity.trim() && !inlineAreaZip.trim())
                  }
                  className="mt-3 px-4 py-1.5 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
                >
                  {savingInlineArea
                    ? t("dashboard.weekend.areaPicker.saving")
                    : t("dashboard.weekend.areaPicker.save")}
                </button>
              </div>
            ) : (
              <EmptyState
                icon={"\u{1F3D5}️"}
                title={t("dashboard.weekend.empty.title")}
                subtitle={t("dashboard.weekend.empty.subtitle")}
              />
            )}
          </Card>
        ) : (
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {weekendSuggestions.map((s) => (
              <WeekendCard
                key={s.uid}
                suggestion={s}
                userId={profile?.uid}
                onVote={() => {}}
                variant="compact"
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  function renderFamilySection() {
    return (
      <section key="family">
        <SectionHeader title={t("dashboard.family.title")} icon="👨‍👩‍👧‍👦" />
        <div className="mt-2 space-y-2">
          {/* Recent Posts */}
          {recentPosts.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1">
                {t("dashboard.family.posts")}
              </p>
              {recentPosts.map((post) => (
                <PostCardCompact
                  key={post.uid}
                  post={post}
                  author={findMember(post.created_by)}
                  members={members}
                />
              ))}
            </div>
          )}

          {/* Recent Chats */}
          {recentChats.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-1 mb-1">
                {t("dashboard.family.chats")}
              </p>
              {recentChats.map((chat) => {
                const lastAuthor = chat.last_reply_uid
                  ? findMember(chat.last_reply_uid)
                  : findMember(chat.created_by);
                const date = fromTimestamp(
                  chat.last_reply_at ?? chat.created_at
                );
                return (
                  <Link
                    key={chat.uid}
                    to={`/chats/${chat.uid}`}
                    className="block"
                  >
                    <Card className="mb-2">
                      <div className="flex items-center gap-3">
                        <div className="flex -space-x-2">
                          {chat.chat_members.slice(0, 3).map((memberId) => {
                            const m = findMember(memberId);
                            return (
                              <Avatar
                                key={memberId}
                                src={m?.profile_url}
                                alt={m?.nickname ?? m?.name ?? ""}
                                size={28}
                                className="ring-2 ring-white dark:ring-gray-900"
                              />
                            );
                          })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500 line-clamp-1">
                            {chat.last_reply_text ?? chat.text}
                          </p>
                        </div>
                        {date && (
                          <span className="text-xs text-gray-400 shrink-0">
                            {relativeDate(date)}
                          </span>
                        )}
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}

          {recentPosts.length === 0 && recentChats.length === 0 && (
            <Card>
              <EmptyState
                icon={"\u{1F4EE}"}
                title={t("dashboard.family.empty.title")}
                subtitle={t("dashboard.family.empty.subtitle")}
              />
            </Card>
          )}
        </div>
      </section>
    );
  }

  function renderSection(sectionId: DashboardSectionId) {
    switch (sectionId) {
      case "today":
        return renderTodaySection();
      case "meals":
        return renderMealsSection();
      case "responsibilities":
        return renderResponsibilitiesSection();
      case "chores":
        return renderChoresSection();
      case "lists":
        return renderListsSection();
      case "weekend":
        return renderWeekendSection();
      case "family":
        return renderFamilySection();
      default:
        return null;
    }
  }

  return (
    <div className="p-4 space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold">
          {t("dashboard.greeting.salutation", { greeting, name })}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {new Date().toLocaleDateString(locale, {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </p>
        <div className="relative mt-1">
          {allHouseholds.length > 1 ? (
            <>
              <button
                type="button"
                onClick={() => setShowHouseholdMenu((v) => !v)}
                className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-[color:var(--accent-color)]"
                aria-haspopup="listbox"
                aria-expanded={showHouseholdMenu}
              >
                <span>{household?.household_label ?? t("dashboard.household.fallback")}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {showHouseholdMenu && (
                <div
                  role="listbox"
                  className="absolute z-10 left-0 mt-1 min-w-[14rem] rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900 shadow-lg overflow-hidden"
                >
                  {allHouseholds.map((hh) => {
                    const isActive = hh.uid === householdId;
                    return (
                      <button
                        key={hh.uid}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => {
                          if (hh.uid && hh.uid !== householdId) {
                            switchHousehold(hh.uid);
                          }
                          setShowHouseholdMenu(false);
                        }}
                        className={`flex items-center justify-between w-full px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-white/5 ${
                          isActive ? "font-semibold" : ""
                        }`}
                      >
                        <span className="truncate">{hh.household_label ?? "Household"}</span>
                        {isActive && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-[color:var(--accent-color)]">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {household?.household_label ?? t("dashboard.household.fallback")}
            </span>
          )}
        </div>
      </div>

      {/* Household Growth Card */}
      {members.length <= 1 && !growthDismissed && (
        <Card>
          <div className="flex items-start gap-3">
            <span className="text-2xl">{"\u{1F46A}"}</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {t("dashboard.growth.title")}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {t("dashboard.growth.body")}
              </p>
              {/* iOS dashboard surfaces these as inline modal CTAs
                  rather than navigating away — matches
                  `Dashboard.swift:545-550` sheet flow. */}
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  onClick={() => setShowAddMemberModal(true)}
                  className="text-xs font-medium"
                  style={{ color: "var(--accent-color, #00C7BE)" }}
                >
                  {t("dashboard.growth.inviteMembers")}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/members")}
                  className="text-xs font-medium"
                  style={{ color: "var(--accent-color, #00C7BE)" }}
                >
                  {t("dashboard.growth.addPet")}
                </button>
              </div>
            </div>
            <button
              onClick={() => setGrowthDismissed(true)}
              aria-label={t("dashboard.growth.dismissAria")}
              className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </Card>
      )}

      {/* Ordered Sections */}
      {sectionOrder.map((sectionId) => renderSection(sectionId))}

      {showAddMemberModal && (
        <AddMemberModal onClose={() => setShowAddMemberModal(false)} />
      )}
    </div>
  );
}

function getGreeting(t: (key: string) => string): string {
  const hour = new Date().getHours();
  if (hour < 12) return t("dashboard.greeting.morning");
  if (hour < 17) return t("dashboard.greeting.afternoon");
  return t("dashboard.greeting.evening");
}

interface ConfirmDialogProps {
  title: string;
  body: string;
  confirmLabel: string;
  destructive?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  destructive,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  const t = (key: string) => key;
  // Translations not threaded through here — the cancel label is
  // localized by callers passing prebuilt content. Keep the cancel
  // button hardcoded to "Cancel" (consistent with rest of app).
  void t;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-5 shadow-xl text-gray-900 dark:text-gray-100">
        <h3 className="font-bold text-base">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{body}</p>
        <div className="mt-4 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold text-white ${
              destructive ? "bg-red-600 hover:bg-red-700" : "bg-[color:var(--accent-color)]"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
